import { createContext, useContext } from "react";
import { darkScrollbar, type PaletteMode } from "@mui/material";
import type { ThemeOptions } from "@mui/material/styles";

export type ThemeMode = PaletteMode;

/**
 * Semantic color tokens for the VS Code "chrome" (activity bar, explorer,
 * tab strip, status bar, hero, book carousel surfaces). Components read these
 * off `theme.palette.vscode.*` instead of hardcoding hex, so every surface
 * flips correctly between light and dark. The markdown content pages don't
 * appear here — they render through MUI Typography/Divider/Paper/Chip/Link and
 * inherit the standard palette (text/background/divider/primary) set below.
 */
export interface VscodePalette {
  /** Vivid brand cyan — safe as a FILL with dark text on top (both modes). */
  accent: string;
  /** Fixed full-page backdrop gradient behind everything. */
  appGradient: string;

  // Activity bar (the slim icon rail on the far left).
  activityBar: string;
  activityIcon: string;
  activityIconHover: string;
  activityBorderActive: string;
  activityBorderInactive: string;

  // Explorer / file-tree panel.
  explorerBg: string;
  treeItemBg: string;
  treeItemBgSelected: string;
  treeItemText: string;
  treeItemTextSelected: string;
  treeFileIcon: string;
  treeHomeLabel: string;

  // Editor tab strip.
  tabBarBg: string;
  tabBorder: string;
  tabActiveBg: string;
  tabInactiveBg: string;
  tabActiveText: string;
  tabInactiveText: string;
  tabIcon: string;
  tabCloseHoverBg: string;
  /** Close "x" color for an inactive tab on hover (active tab reuses tabActiveText). */
  tabCloseHoverTextInactive: string;
  scrollbarThumb: string;

  // Status bar (the thin footer).
  statusBarBg: string;
  statusBarText: string;
  statusBarRemoteBg: string;
  statusBarRemoteHoverBg: string;
  statusBarItemHoverBg: string;

  // Content accents.
  heroTitle: string;
  asciiArt: string;
  mutedText: string;
  faintText: string;
  commentGreen: string;

  // Glass surfaces used by the book carousel (settings popover / info panel).
  surface: string;
  surfaceBorder: string;
  surfaceText: string;
  surfaceTextStrong: string;
  surfaceHoverBg: string;
}

declare module "@mui/material/styles" {
  interface Palette {
    vscode: VscodePalette;
  }
  interface PaletteOptions {
    vscode?: VscodePalette;
  }
}

const darkTokens: VscodePalette = {
  accent: "#40c4ff",
  appGradient: "linear-gradient(160deg, #2a2a2a 0%, #111111 40%, #000000 100%)",

  activityBar: "#000000",
  activityIcon: "#858585",
  activityIconHover: "#ffffff",
  activityBorderActive: "#ffffff",
  activityBorderInactive: "#333333",

  explorerBg: "#141414",
  treeItemBg: "#141414",
  treeItemBgSelected: "rgba(192,192,192,0.12)",
  treeItemText: "#909090",
  treeItemTextSelected: "#d0d0d0",
  treeFileIcon: "#8a8a8a",
  treeHomeLabel: "#bdc3cf",

  tabBarBg: "#141414",
  tabBorder: "#1a1a1a",
  tabActiveBg: "#0a0a0a",
  tabInactiveBg: "#1a1a1a",
  tabActiveText: "#c0c0c0",
  tabInactiveText: "#666666",
  tabIcon: "#8a8a8a",
  tabCloseHoverBg: "#2a2a2a",
  tabCloseHoverTextInactive: "#888888",
  scrollbarThumb: "#404040",

  statusBarBg: "#000000",
  statusBarText: "#ffffff",
  statusBarRemoteBg: "#333333",
  statusBarRemoteHoverBg: "#444444",
  statusBarItemHoverBg: "#1a1a1a",

  heroTitle: "#f0f0f0",
  asciiArt: "#b0b0b0",
  mutedText: "rgba(192,192,192,0.6)",
  faintText: "rgba(192,192,192,0.4)",
  commentGreen: "#6a9955",

  surface: "rgba(16,16,16,0.92)",
  surfaceBorder: "rgba(192,192,192,0.16)",
  surfaceText: "rgba(220,220,220,0.82)",
  surfaceTextStrong: "#f2f2f2",
  surfaceHoverBg: "rgba(30,30,30,0.8)",
};

const lightTokens: VscodePalette = {
  accent: "#40c4ff",
  appGradient: "linear-gradient(160deg, #ffffff 0%, #f6f6f6 45%, #ececec 100%)",

  activityBar: "#e8e8e8",
  activityIcon: "#5f5f5f",
  activityIconHover: "#1f1f1f",
  activityBorderActive: "#1f1f1f",
  activityBorderInactive: "#cfcfcf",

  explorerBg: "#f3f3f3",
  treeItemBg: "#f3f3f3",
  treeItemBgSelected: "#d6ebff",
  treeItemText: "#3c3c3c",
  treeItemTextSelected: "#1f1f1f",
  treeFileIcon: "#6c6c6c",
  treeHomeLabel: "#3c3c3c",

  tabBarBg: "#ececec",
  tabBorder: "#dcdcdc",
  tabActiveBg: "#ffffff",
  tabInactiveBg: "#ececec",
  tabActiveText: "#1f1f1f",
  tabInactiveText: "#5f5f5f",
  tabIcon: "#6c6c6c",
  tabCloseHoverBg: "#c4c4c4",
  tabCloseHoverTextInactive: "#3c3c3c",
  scrollbarThumb: "#bdbdbd",

  statusBarBg: "#dcdcdc",
  statusBarText: "#3c3c3c",
  statusBarRemoteBg: "#c4c4c4",
  statusBarRemoteHoverBg: "#b3b3b3",
  statusBarItemHoverBg: "#cccccc",

  heroTitle: "#1a1a1a",
  asciiArt: "#aab0b6",
  mutedText: "#57606a",
  faintText: "#8b949e",
  commentGreen: "#2e7d32",

  surface: "rgba(255,255,255,0.95)",
  surfaceBorder: "rgba(0,0,0,0.14)",
  surfaceText: "rgba(40,40,40,0.85)",
  surfaceTextStrong: "#1f1f1f",
  surfaceHoverBg: "rgba(0,0,0,0.06)",
};

export const vscodeTokens: Record<ThemeMode, VscodePalette> = {
  dark: darkTokens,
  light: lightTokens,
};

export const getDesignTokens = (mode: ThemeMode): ThemeOptions => {
  const v = vscodeTokens[mode];
  const isLight = mode === "light";

  return {
    typography: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontWeight: 600, letterSpacing: "-0.02em" },
      h2: { fontWeight: 600, letterSpacing: "-0.01em" },
      h3: { fontWeight: 600, letterSpacing: "-0.01em" },
      body1: { fontWeight: 400, letterSpacing: "0.01em" },
      body2: { fontWeight: 400, letterSpacing: "0.01em" },
    },
    palette: {
      mode,
      primary: { main: isLight ? "#0e7490" : "#40c4ff" },
      background: isLight
        ? { default: "#ffffff", paper: "#ffffff" }
        : { default: "#0a0a0a", paper: "#1a1a1a" },
      text: isLight
        ? { primary: "#1f1f1f", secondary: "#5f5f5f" }
        : { primary: "#ffffff", secondary: "rgba(255,255,255,0.7)" },
      divider: isLight ? "rgba(0,0,0,0.12)" : "rgba(192,192,192,0.12)",
      vscode: v,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { backgroundColor: isLight ? "#ffffff" : "#000000" },
          body: {
            backgroundColor: isLight ? "#ffffff" : "#0a0a0a",
            ...(isLight ? {} : darkScrollbar()),
          },
          "body::before": {
            content: '""',
            position: "fixed",
            inset: 0,
            zIndex: -2,
            pointerEvents: "none",
            backgroundImage: v.appGradient,
            transform: "translateZ(0)",
          },
        },
      },
    },
  };
};

/** Context that exposes the current mode and a toggle, provided by <App />. */
export interface ColorModeContextValue {
  mode: ThemeMode;
  toggle: () => void;
}

export const ColorModeContext = createContext<ColorModeContextValue>({
  mode: "dark",
  toggle: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

export const THEME_STORAGE_KEY = "themeMode";

/** Resolve the startup mode: saved choice → system preference → dark. */
export const getInitialMode = (): ThemeMode => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: light)").matches
    ) {
      return "light";
    }
  } catch (e) {
    /* localStorage / matchMedia may be unavailable */
  }
  return "dark";
};
